import React, { useMemo, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import Style from './summaryDetails.module.css'
import useSummaryDetails, { EMPTY_FILTERS } from './useSummaryDetails'
import RecordsTable, { isError } from './Components/RecordsTable'
import Pager from './Components/Pager'
import { ActiveFilterChips, ID_FIELDS, IdFilterPanel } from './Components/IdFilters'
import DateRangeFilter from '../../Components/DateRangeFilter'
import { rangeFromParams, rangeToParams } from '../../summaryFormat'
import PageLoader from '../../../../SharedComponents/PageState/PageLoader'
import PageNotice from '../../../../SharedComponents/PageState/PageNotice'
import { describeConnection, useConnection } from '../../../../../features/connections/connectionsApi'
import { DIRECTION, directionFor, recordStatusFor } from '../../../../../features/syncReports/recordStatus'
import useEntitySummaries from '../../../../../features/syncReports/useEntitySummaries'
import { CLIENT_PERMISSION } from '../../../../../features/clientUsers/clientAccess'
import { useSubscriptionAccess } from '../../../../../features/clientUsers/useClientAccess'
import { PATH, paths } from '../../../../../app/routes'

const SummaryDetails = () => {
    const history = useHistory()
    const location = useLocation()
    const { connectorId } = useParams()
    const params = useMemo(() => new URLSearchParams(location.search), [location.search])
    const direction = directionFor(params.get('direction'))
    const statusKey = params.get('status') || 'total'
    const recordStatus = recordStatusFor(statusKey)
    const entityId = params.get('entityId')
    const range = useMemo(() => rangeFromParams(params), [params])
    const [showFilters, setShowFilters] = useState(false)

    const { connection, isLoading: isConnectionLoading } = useConnection(connectorId)
    const connector = describeConnection(connection)
    const summaries = useEntitySummaries(connection, range)
    const { can } = useSubscriptionAccess(connector && connector.subscriptionId)
    const canResync = can(CLIENT_PERMISSION.SYNC_RETRY)

    const instanceId = connector ? connector.instanceId : null
    const endpointId = connector ? (direction === DIRECTION.target ? connector.target.id : connector.source.id) : null
    const request = useMemo(() => (instanceId
        ? {
            instanceId,
            endpointId,
            requestType: direction.requestType,
            statusIds: recordStatus.statusIds,
            entityIds: entityId ? [Number(entityId)] : []
        }
        : null), [instanceId, endpointId, direction, recordStatus, entityId])

    const changeRange = (nextRange) => {
        history.replace(paths.summaryDetails(connectorId, {
            direction: direction.key,
            status: statusKey,
            entityId: entityId || undefined,
            ...rangeToParams(nextRange)
        }))
    }

    const details = useSummaryDetails(request, range, changeRange)
    const {
        filters, setFilters, page, goToPage, pageSize, setPageSize,
        records, totalRecords, isLoading, loadError, reload,
        selected, setSelected, toggleSelected, resync, isResyncing, status, dismissStatus
    } = details

    if (!connector) {
        return isConnectionLoading ? (
            <PageLoader message="Loading records..." />
        ) : (
            <PageNotice
                title="Connector not found"
                hint="It may have been removed, or the link is incomplete."
                linkTo={PATH.MY_PRODUCTS}
                linkLabel="Go to My Products"
            />
        )
    }

    const entityRows = direction === DIRECTION.target ? summaries.target : summaries.source
    const entity = entityId ? entityRows.find((row) => String(row.entityId) === entityId) : null
    const fromName = direction === DIRECTION.target ? connector.target.shortName : connector.source.shortName
    const toName = direction === DIRECTION.target ? connector.source.shortName : connector.target.shortName
    const summaryLink = paths.viewSummary(connectorId, { direction: direction.key, ...rangeToParams(range) })

    const idFilterCount = ID_FIELDS.filter((field) => filters[field.key].trim() !== '').length
    const hasFilters = idFilterCount > 0 || filters.range !== null
    const failedOnPage = records.filter(isError).length

    return (
        <div className="main-content mn-summaryDetails">
            <div className="page-content custompaddingdash">
                <div className={`container-fluid ${Style.page}`}>
                    <header className={Style.pageHeader}>
                        <div className={Style.pageHeading}>
                            <ol className={`breadcrumb ${Style.breadcrumb}`}>
                                <li className="breadcrumb-item"><Link to={PATH.DASHBOARD}>Dashboard</Link></li>
                                <li className="breadcrumb-item"><Link to={summaryLink}>Sync summary</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Records</li>
                            </ol>
                            <h1 className={Style.pageTitle}>{recordStatus.title}</h1>
                            <p className={Style.pageSubtitle}>
                                {[entity ? entity.entityName : (entityId ? null : 'All entities'), `${fromName} → ${toName}`, range ? range.label : 'All time']
                                    .filter(Boolean)
                                    .join(' · ')}
                            </p>
                        </div>
                        <DateRangeFilter
                            range={filters.range}
                            onChange={(nextRange) => setFilters({ ...filters, range: nextRange })}
                            disabled={isLoading}
                        />
                    </header>

                    {status ? (
                        <div className={[Style.banner, status.tone === 'success' ? Style.bannerSuccess : Style.bannerError].join(' ')} role={status.tone === 'success' ? 'status' : 'alert'}>
                            <i className={status.tone === 'success' ? 'uil-check-circle' : 'uil-exclamation-triangle'} aria-hidden="true" />
                            <span className={Style.bannerText}>{status.message}</span>
                            <button type="button" className={Style.bannerClose} aria-label="Dismiss message" onClick={dismissStatus}>&times;</button>
                        </div>
                    ) : null}

                    <section className={Style.card}>
                        <header className={Style.cardHeader}>
                            <div className={Style.cardTitle}>
                                <h2 className={Style.cardTitleText}>Records</h2>
                                {!isLoading && !loadError ? <span className={Style.countChip}>{totalRecords.toLocaleString()}</span> : null}
                            </div>
                            <div className={Style.toolbar}>
                                <button
                                    type="button"
                                    className={[Style.ghostButton, showFilters || idFilterCount > 0 ? Style.ghostButtonActive : ''].join(' ')}
                                    aria-expanded={showFilters}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <i className="uil-filter" aria-hidden="true" /> Find by ID
                                    {idFilterCount > 0 ? <span className={Style.badge}>{idFilterCount}</span> : null}
                                </button>
                                <button type="button" className={Style.ghostButton} onClick={reload} disabled={isLoading} aria-label="Refresh">
                                    <i className="uil-redo" aria-hidden="true" /> Refresh
                                </button>
                                {canResync ? <button
                                    type="button"
                                    className={Style.primaryButton}
                                    disabled={selected.length === 0 || isResyncing}
                                    onClick={resync}
                                    title={selected.length === 0 ? 'Select failed records to re-sync them' : undefined}
                                >
                                    {isResyncing ? <span className={Style.buttonSpinner} aria-hidden="true" /> : <i className="uil-sync" aria-hidden="true" />}
                                    {selected.length > 0 ? `Re-sync ${selected.length} selected` : 'Re-sync selected'}
                                </button> : null}
                            </div>
                        </header>

                        {showFilters ? (
                            <IdFilterPanel
                                filters={filters}
                                onApply={setFilters}
                                onClose={() => setShowFilters(false)}
                            />
                        ) : null}

                        <ActiveFilterChips filters={filters} onChange={setFilters} />

                        {selected.length > 0 ? (
                            <div className={Style.selectionBar}>
                                <strong>{selected.length}</strong> of {failedOnPage} failed {failedOnPage === 1 ? 'record' : 'records'} on this page selected
                                <button type="button" className={Style.linkButton} onClick={() => setSelected([])}>Clear selection</button>
                            </div>
                        ) : null}

                        <RecordsTable
                            records={records}
                            canResync={canResync}
                            isLoading={isLoading}
                            loadError={loadError}
                            onRetry={reload}
                            selected={selected}
                            setSelected={setSelected}
                            toggleSelected={toggleSelected}
                            hasFilters={hasFilters}
                            onClearFilters={() => setFilters(EMPTY_FILTERS)}
                            pageSize={pageSize}
                        />

                        {!loadError && totalRecords > 0 ? (
                            <Pager
                                page={page}
                                pageSize={pageSize}
                                totalRecords={totalRecords}
                                onPage={goToPage}
                                onPageSize={setPageSize}
                                disabled={isLoading}
                            />
                        ) : null}
                    </section>
                </div>
            </div>
        </div>
    )
}

export default SummaryDetails
